/**
 * Apply Device Sync records with catalog → trip → chunk → granular LWW precedence.
 * Port of iOS DeviceSyncApplyService (without SwiftData persistence).
 */

import type { DeviceSyncRecord } from '../deviceSyncService';
import { decodeCityVisit, mealTitlesFromCityVisit } from './encodeCityVisit';
import { DeviceSyncRecordType, type DeviceSyncCityVisit } from './types';

export interface AppliedTrip {
  id: string;
  name: string;
  chunkCount: number;
  cityVisits: Map<string, DeviceSyncCityVisit>;
}

export interface DeviceSyncApplyOutcome {
  touchedTripCount: number;
  incompleteCatalogTripIds: string[];
  trips: Map<string, AppliedTrip>;
}

type PendingSource =
  | { kind: 'chunk'; cityDict: Record<string, unknown>; attachmentRefs: unknown[] }
  | {
      kind: 'granular';
      body: Record<string, unknown>;
      attachmentRefs: unknown[];
      deleted: boolean;
    };

interface PendingCitySync {
  tripId: string;
  cityId: string;
  updatedAt: number;
  source: PendingSource;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function normalizeId(id: string | null | undefined): string {
  const raw = String(id ?? '').trim();
  if (!raw) return '';
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw)) {
    return raw.toLowerCase();
  }
  return raw;
}

function updatedAtMs(raw: string | undefined): number {
  if (!raw) return Number.NEGATIVE_INFINITY;
  const t = Date.parse(raw);
  return Number.isFinite(t) ? t : Number.NEGATIVE_INFINITY;
}

function pickLatestCatalog(records: DeviceSyncRecord[]): DeviceSyncRecord | null {
  let best: DeviceSyncRecord | null = null;
  let bestMs = Number.NEGATIVE_INFINITY;
  for (const r of records) {
    if (r.type !== DeviceSyncRecordType.catalog) continue;
    const ms = updatedAtMs(r.updatedAt);
    if (ms >= bestMs) {
      best = r;
      bestMs = ms;
    }
  }
  return best;
}

function tripIdsReferencedWithoutCatalog(records: DeviceSyncRecord[]): Set<string> {
  const tripIds = new Set<string>();
  for (const record of records) {
    if (
      record.type === DeviceSyncRecordType.trip ||
      record.type === DeviceSyncRecordType.cityChunk ||
      record.type === DeviceSyncRecordType.cityVisit
    ) {
      const tripId = normalizeId(String(record.body?.tripId ?? ''));
      if (tripId) tripIds.add(tripId);
    }
  }
  return tripIds;
}

function catalogSkipTripIds(
  catalog: DeviceSyncRecord | null,
  records: DeviceSyncRecord[],
  skipAllWhenCatalogMissing: boolean
): Set<string> {
  const skip = new Set<string>();
  const trips = catalog?.body?.trips;
  if (Array.isArray(trips)) {
    for (const entry of trips) {
      const o = asRecord(entry);
      const id = normalizeId(String(o.tripId ?? ''));
      if (!id) continue;
      // Catalog `deleted: true` must NOT skip — otherwise trip-v1 tombstones are
      // ignored (cross-device delete never applies). Incomplete uploads still skip.
      if (o.deleted === true) continue;
      if (o.syncComplete === false) {
        skip.add(id);
      }
    }
  } else if (skipAllWhenCatalogMissing) {
    // Owner full sync: without a catalog, do not materialize trip bodies.
    // Shared canonical pulls often omit catalog — callers pass skipAllWhenCatalogMissing: false.
    Array.from(tripIdsReferencedWithoutCatalog(records)).forEach((id) => {
      skip.add(id);
    });
  }
  return skip;
}

/** iOS DeviceSyncTripHeaderPolicy.shouldMaterialize */
function shouldMaterializeTripHeader(
  body: Record<string, unknown>,
  allowEmptyChunkShell: boolean
): boolean {
  if (allowEmptyChunkShell) return true;
  const chunkCount =
    typeof body.chunkCount === 'number'
      ? body.chunkCount
      : Number(body.chunkCount) || 0;
  if (chunkCount !== 0) return true;
  return body.citySyncMode === 'granular';
}

/** Trip ids marked deleted in the latest catalog (tombstone even if trip-v1 is absent). */
function catalogDeletedTripIds(catalog: DeviceSyncRecord | null): Set<string> {
  const deleted = new Set<string>();
  const trips = catalog?.body?.trips;
  if (!Array.isArray(trips)) return deleted;
  for (const entry of trips) {
    const o = asRecord(entry);
    const id = normalizeId(String(o.tripId ?? ''));
    if (!id) continue;
    if (o.deleted === true) deleted.add(id);
  }
  return deleted;
}

function incompleteCatalogTripIds(catalog: DeviceSyncRecord | null): string[] {
  const trips = catalog?.body?.trips;
  if (!Array.isArray(trips)) return [];
  const incomplete: string[] = [];
  for (const entry of trips) {
    const o = asRecord(entry);
    const id = normalizeId(String(o.tripId ?? ''));
    if (!id || o.deleted === true) continue;
    if (o.syncComplete === false) incomplete.push(id);
  }
  return incomplete;
}

function registerCityUpdate(
  pending: Map<string, PendingCitySync>,
  tripId: string,
  cityId: string,
  updatedAt: string | undefined,
  source: PendingSource
): void {
  const date = updatedAtMs(updatedAt);
  const existing = pending.get(cityId);
  if (existing) {
    if (date > existing.updatedAt) {
      pending.set(cityId, { tripId, cityId, updatedAt: date, source });
    } else if (date === existing.updatedAt && source.kind === 'granular') {
      pending.set(cityId, { tripId, cityId, updatedAt: date, source });
    }
  } else {
    pending.set(cityId, { tripId, cityId, updatedAt: date, source });
  }
}

export interface ApplyDeviceSyncOptions {
  /** Seed existing trips (e.g. local cache before pull). */
  initialTrips?: Map<string, AppliedTrip>;
  /**
   * Shared invitee pull (iOS `allowEmptyChunkShell`).
   * When true: do not skip all trips if catalog is missing, and materialize
   * trip headers even when chunkCount is 0.
   */
  allowEmptyChunkShell?: boolean;
}

/**
 * Apply a batch of Device Sync records into an in-memory trip map.
 * Matches iOS DeviceSyncApplyService precedence rules.
 */
export function applyDeviceSyncRecords(
  records: DeviceSyncRecord[],
  options: ApplyDeviceSyncOptions = {}
): DeviceSyncApplyOutcome {
  const allowEmptyChunkShell = options.allowEmptyChunkShell === true;
  const catalog = pickLatestCatalog(records);
  const skipTripIds = catalogSkipTripIds(catalog, records, !allowEmptyChunkShell);
  const incomplete = incompleteCatalogTripIds(catalog);

  const trips = new Map<string, AppliedTrip>();
  if (options.initialTrips) {
    Array.from(options.initialTrips.entries()).forEach(([id, trip]) => {
      trips.set(id, {
        ...trip,
        cityVisits: new Map(trip.cityVisits),
      });
    });
  }

  const pendingCities = new Map<string, PendingCitySync>();
  const tripHeaders = new Map<
    string,
    { body: Record<string, unknown>; updatedAt?: string }
  >();
  const tripTombstones = new Set<string>();

  for (const record of records) {
    if (record.type !== DeviceSyncRecordType.trip) continue;
    const tripId = normalizeId(String(record.body?.tripId ?? ''));
    if (!tripId || skipTripIds.has(tripId)) continue;
    if (record.body?.deleted === true) {
      tripTombstones.add(tripId);
      continue;
    }
    const body = asRecord(record.body);
    if (!shouldMaterializeTripHeader(body, allowEmptyChunkShell)) continue;
    tripHeaders.set(tripId, { body, updatedAt: record.updatedAt });
  }

  Array.from(catalogDeletedTripIds(catalog)).forEach((id) => {
    tripTombstones.add(id);
  });

  const chunkRecords = records
    .filter((r) => r.type === DeviceSyncRecordType.cityChunk)
    .slice()
    .sort((lhs, rhs) => {
      const lIdx =
        typeof lhs.body?.chunkIndex === 'number'
          ? lhs.body.chunkIndex
          : Number(lhs.body?.chunkIndex) || 0;
      const rIdx =
        typeof rhs.body?.chunkIndex === 'number'
          ? rhs.body.chunkIndex
          : Number(rhs.body?.chunkIndex) || 0;
      if (lIdx !== rIdx) return lIdx - rIdx;
      return updatedAtMs(lhs.updatedAt) - updatedAtMs(rhs.updatedAt);
    });

  for (const record of chunkRecords) {
    const tripId = normalizeId(String(record.body?.tripId ?? ''));
    if (!tripId || skipTripIds.has(tripId)) continue;
    const cities = record.body?.cityVisits;
    if (!Array.isArray(cities)) continue;
    const refs = Array.isArray(record.body?.attachmentRefs)
      ? record.body.attachmentRefs
      : [];
    for (const cityDict of cities) {
      const o = asRecord(cityDict);
      const cityId = normalizeId(String(o.cityVisitId ?? o.id ?? ''));
      if (!cityId) continue;
      registerCityUpdate(pendingCities, tripId, cityId, record.updatedAt, {
        kind: 'chunk',
        cityDict: o,
        attachmentRefs: refs,
      });
    }
  }

  for (const record of records) {
    if (record.type !== DeviceSyncRecordType.cityVisit) continue;
    const tripId = normalizeId(String(record.body?.tripId ?? ''));
    const cityId = normalizeId(String(record.body?.cityVisitId ?? ''));
    if (!tripId || !cityId || skipTripIds.has(tripId)) continue;
    const refs = Array.isArray(record.body?.attachmentRefs)
      ? record.body.attachmentRefs
      : [];
    registerCityUpdate(pendingCities, tripId, cityId, record.updatedAt, {
      kind: 'granular',
      body: asRecord(record.body),
      attachmentRefs: refs,
      deleted: record.body?.deleted === true,
    });
  }

  const allTripIds = new Set<string>();
  Array.from(tripHeaders.keys()).forEach((id) => allTripIds.add(id));
  Array.from(tripTombstones).forEach((id) => allTripIds.add(id));
  Array.from(pendingCities.values()).forEach((pending) => allTripIds.add(pending.tripId));

  const touchedTrips = new Set<string>();

  Array.from(allTripIds)
    .sort()
    .forEach((tripId) => {
    if (skipTripIds.has(tripId)) return;

    if (tripTombstones.has(tripId)) {
      trips.delete(tripId);
      touchedTrips.add(tripId);
    } else if (tripHeaders.has(tripId)) {
      const header = tripHeaders.get(tripId)!;
      const existing = trips.get(tripId);
      trips.set(tripId, {
        id: tripId,
        name: String(header.body.name ?? existing?.name ?? ''),
        chunkCount:
          typeof header.body.chunkCount === 'number'
            ? header.body.chunkCount
            : existing?.chunkCount ?? 0,
        cityVisits: existing?.cityVisits ?? new Map(),
      });
      touchedTrips.add(tripId);
    }

    const citiesForTrip = Array.from(pendingCities.values()).filter(
      (p) => p.tripId === tripId
    );
    citiesForTrip.forEach((pending) => {
      let trip = trips.get(tripId);
      if (!trip) {
        trip = {
          id: tripId,
          name: '',
          chunkCount: 0,
          cityVisits: new Map(),
        };
        trips.set(tripId, trip);
      }

      if (pending.source.kind === 'chunk') {
        const visit = decodeCityVisit(pending.source.cityDict);
        trip.cityVisits.set(pending.cityId, visit);
        touchedTrips.add(tripId);
      } else if (pending.source.deleted) {
        trip.cityVisits.delete(pending.cityId);
        touchedTrips.add(tripId);
      } else {
        const visit = decodeCityVisit(pending.source.body);
        trip.cityVisits.set(pending.cityId, visit);
        touchedTrips.add(tripId);
      }
    });
  });

  return {
    touchedTripCount: touchedTrips.size,
    incompleteCatalogTripIds: incomplete,
    trips,
  };
}

/** Helper for tests: meal titles on a trip after apply. */
export function mealTitlesForTrip(
  outcome: DeviceSyncApplyOutcome,
  tripId: string
): string[] {
  const trip = outcome.trips.get(normalizeId(tripId));
  if (!trip) return [];
  return Array.from(trip.cityVisits.values()).flatMap((cv) => mealTitlesFromCityVisit(cv));
}

export function seedAppliedTrip(
  tripId: string,
  cityId: string,
  cityName = 'Rome'
): Map<string, AppliedTrip> {
  const cityVisits = new Map<string, DeviceSyncCityVisit>();
  cityVisits.set(normalizeId(cityId), {
    cityVisitId: normalizeId(cityId),
    cityName,
    latitude: 41.9028,
    longitude: 12.4964,
    timeZoneIdentifier: 'Europe/Rome',
    arrivalDate: '2026-06-01T00:00:00Z',
    departureDate: '2026-06-03T00:00:00Z',
    hotelStays: [],
    options: [],
    attachmentFileIds: [],
    days: [],
  });
  const trips = new Map<string, AppliedTrip>();
  trips.set(normalizeId(tripId), {
    id: normalizeId(tripId),
    name: 'Italy',
    chunkCount: 0,
    cityVisits,
  });
  return trips;
}
